import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import log from "loglevel";
//Material imports
import { ThemeProvider } from "@mui/material/styles";
//Limitless imports

import {
    useKnowledgeLibraryContext,
    VerticalTabWithPaperContainer,
    KnowledgeSearchInput,
    LoadingSpinner
} from "@limitless/portal";
import {
    useExpertProfileContext,
    useMutationObserver,
    useExpertSupportConversationHistoryContext,
    useExpertSupportMessageCreationContext
} from "@limitless/expert";
import Grid from "@limitless/modern/layout/Grid";
import Typography from "@limitless/modern/data-display/typography/Typography";
import { modernTheme } from "@limitless/themeing/modern-theme/ModernTheme";
import VerticalTab from "@limitless/modern/navigation/vertical-tabs/VerticalTabs";
import Button from "@limitless/modern/inputs/button/Button";
import Divider from "@limitless/modern/data-display/divider/Divider";
import { useAppConfig, useNotificationContext } from "@limitless/common/hooks";
import ConversationHistory from "../components/conversation-history/ConversationHistory";
import KnowledgeArticles from "../components/knowledge-articles/KnowledgeArticles";
import GetInTouchScreen from "./GetInTouchScreen";
import RespondAnnouncement from "./RespondAnnouncement";
import ReceiveMessageScreen from "./ReceiveMessageScreen";
import { LIMITLESS } from "@limitless/libraries/src/portal/utils/Constants";

const ContactUsScreen = () => {
    const history = useHistory();

    const { getExpertSupportDetails } = useAppConfig();
    const {
        faqKnowledgeBase,
        privateSupport: { apiKey, tenantName, groupName, locale },
        llmLandingPageUrl,

        privateSupport
    } = getExpertSupportDetails();

    const { unReadMessageCount, fetchSupportMessageHistory } = useExpertSupportConversationHistoryContext();
    const { updateSelectedLibrary, libraryResults } = useKnowledgeLibraryContext();
    const { addInfoNotification } = useNotificationContext();
    const {
        hasPressed,
        error,
        setLoading,
        isLoading,
        selectedTabIndex,
        setSelectedTabIndex,
        showLLMLanding,
        setShowLLMLanding,
        isFabVisible,
        setIsFabVisible,
        submitMessage,
        clearMessage,
        isReceived,
        getInTouchScreen,
        setGetinTouchScreen,
        announcementMessage,
        queryTopic,
        queryDescription,
        announcementRouteState,
        setAnnouncementRouteState,
        handleConversationHistory,
        handleSubmitAnnouncementMessage,
        announcementBody,
        setIsReceived,
        setSubmitMessage
    } = useExpertSupportMessageCreationContext();

    const { hasSearched, faqs, searchResults } = libraryResults;
    const articleList = hasSearched ? searchResults : faqs;

    const { isDarkMode, profile } = useExpertProfileContext();

    const supportedGroups = profile.expertSupportDetails?.supportedGroups
        .filter(item => item.displayName)
        .sort((a, b) => {
            //  - "0" for Limitless, "1" for others (so Limitless always first)
            //  - then supportGroupTenantName
            //  - then displayName
            const keyA = `${a.supportGroupTenantName === LIMITLESS ? "0" : "1"}-${a.supportGroupTenantName}-${
                a.displayName
            }`;
            const keyB = `${b.supportGroupTenantName === LIMITLESS ? "0" : "1"}-${b.supportGroupTenantName}-${
                b.displayName
            }`;

            return keyA.localeCompare(keyB);
        })
        .map(item => ({
            label: item.displayName,
            value: item.groupName
        }));

    const { email, firstName, lastName, userId, groups, emailHash } = profile;
    const [messageToHandle, setMessageToHandle] = React.useState(null);

    const llmRootRef = React.useRef();
    const llmLandingRootRef = React.useRef();
    const [messageId, setMessageId] = React.useState(null);
    const location = useLocation();

    let queryString = useLocation()?.search;
    const theme = React.useMemo(() => modernTheme(isDarkMode ? "dark" : "light"), [isDarkMode]);

    const CONTACT_US_TABS = React.useMemo(
        () => [
            { label: "FAQ", value: "FAQ" },
            {
                label: "Conversation History",
                value: "Conversation History",
                badgeCount: 1,
                badgeContent: unReadMessageCount
            }
        ],
        [unReadMessageCount]
    );

    const customerSecuredData = React.useMemo(() => {
        const secureData = [];
        let tenantMembershipList = groups.map(group => group.tenantName);
        tenantMembershipList = [...new Set(tenantMembershipList)];

        if (userId) {
            secureData.push({
                valueName: "User ID",
                value: userId
            });
        }

        if (firstName) {
            secureData.push({
                valueName: "First Name",
                value: firstName
            });
        }

        if (lastName) {
            secureData.push({
                valueName: "Last Name Initial",
                value: lastName?.charAt(0)
            });
        }

        secureData.push({
            valueName: "Tenant Membership",
            value: tenantMembershipList.join(",")
        });

        return secureData;
    }, [groups]);

    React.useEffect(() => {
        if (messageToHandle) {
            handleMessageClickState(messageToHandle);
            setMessageToHandle(null); // Reset after handling
        }
    }, [queryString, isFabVisible, showLLMLanding, messageToHandle]);

    React.useEffect(() => {
        if (history?.location?.state && history?.location.state?.type !== "messageId") {
            setAnnouncementRouteState(history.location.state);

            if (showLLMLanding) {
                setShowLLMLanding(false);
            }
        }
    }, [history?.location?.state, isReceived, announcementMessage]);

    React.useEffect(() => {
        if (announcementRouteState) {
            setSelectedTabIndex(1);
        }
    }, [announcementRouteState]);

    React.useEffect(() => {
        // Load Limitless Expert Support Knowledge Base
        updateSelectedLibrary(faqKnowledgeBase);
    }, []);

    React.useEffect(() => {
        if (hasSearched && selectedTabIndex === 1) {
            setSelectedTabIndex(0);
        }
    }, [hasSearched]);

    React.useEffect(() => {
        if (isFabVisible) {
            setIsFabVisible(false);
        }
        if (selectedTabIndex === 1) {
            fetchSupportMessageHistory(email);
        }
    }, [selectedTabIndex]);

    React.useEffect(() => {
        if (queryString.includes("emailHash")) {
            const emailHashFromQueryParams = new URLSearchParams(queryString).get("emailHash");
            if (emailHashFromQueryParams === emailHash) {
                setSelectedTabIndex(1);
                setShowLLMLanding(true);
            } else if (profile.emailHash) {
                addInfoNotification("You are not authorised to view this message");
            }
        }
    }, [queryString, profile.emailHash]);

    React.useEffect(() => {
        if (location.state?.messageId && location.state?.type === "messageId") {
            setMessageId(location.state.messageId);

            setSelectedTabIndex(0);
        }
    }, [location.state?.messageId, location.state?.type]);

    React.useEffect(() => {
        if ((queryString.includes("messageId") || announcementRouteState) && isFabVisible) {
            setShowLLMLanding(false);
            setLoading(false);
        }
    }, [isDarkMode]);

    React.useEffect(() => {
        if ((isFabVisible && selectedTabIndex === 0) || (isReceived && selectedTabIndex === 0)) {
            setSelectedTabIndex(1);
            setShowLLMLanding(false);
        }
    }, [isFabVisible, isReceived]);

    React.useEffect(() => {
        if (!showLLMLanding) {
            removeLiveMessengerScriptTag();
        }
        if (showLLMLanding && !isFabVisible) {
            const llmScoutNode = document.createElement("script");
            llmScoutNode.setAttribute("type", "text/javascript");
            llmScoutNode.setAttribute("src", llmLandingPageUrl);
            llmScoutNode.onload = () => {
                window.handleFAQClick = () => {
                    setShowLLMLanding(false);
                };
            };
            const bodyNodes = document.getElementsByTagName("body");
            const bodyNode = bodyNodes[bodyNodes.length - 1];

            bodyNode.appendChild(llmScoutNode);
        }
    }, [showLLMLanding, isDarkMode, submitMessage, isFabVisible, isReceived, getInTouchScreen]);

    const removeLiveMessengerScriptTag = () => {
        const scriptTags = document.querySelectorAll("script");
        scriptTags.forEach((_, index) => {
            const sourceString = String(scriptTags[index].src);
            if (
                sourceString.includes("live-messenger-landing") ||
                sourceString.includes(":3015") ||
                sourceString.includes("live-messenger")
            ) {
                document.body.removeChild(scriptTags[index]);
            }
        });

        const currentStyles = document.querySelectorAll("style");
        // Remove styles
        [].slice.call(currentStyles).map((style, index) => {
            const html = String(style.outerHTML);

            if (html.includes("landing") || (!isFabVisible && html.includes("llm"))) {
                try {
                    document.head.removeChild(currentStyles[index]);
                } catch (error) {
                    log.warn(error);
                }
            }
        });
    };

    const handleTabChange = (e, categoryIndex) => {
        clearMessage();
        setSubmitMessage(false);
        setIsReceived(false);
        setAnnouncementRouteState(null);
        setSelectedTabIndex(categoryIndex);
        if (queryString) {
            history.push({ pathname: "/contactusscreen" });
        }
    };

    const mutationObserverCallback = mutationList => {
        mutationList.forEach(element => {
            if (element.target.id === "fabOpenLiveMessenger") {
                setIsFabVisible(true);
                setLoading(false);
            }
        });
    };

    //Mutation observer hook call
    useMutationObserver(llmRootRef, mutationList => mutationObserverCallback(mutationList));
    useMutationObserver(llmLandingRootRef, mutationList => {
        if (mutationList.length > 0) {
            setLoading(false);
        }
    });

    const tabContent = React.useMemo(
        () => (
            <>
                <Grid sx={{ padding: "48px 34px 0px 48px" }}>
                    <KnowledgeSearchInput variant="standard" />
                </Grid>
                <VerticalTab items={CONTACT_US_TABS} value={selectedTabIndex} onChange={handleTabChange} />
            </>
        ),
        [selectedTabIndex, unReadMessageCount]
    );

    const handleMessageClickState = message => {
        if (queryString === "" && !isFabVisible) {
            history.push({
                pathname: "/contactusscreen",
                search: `?messageId=${message.messageId}&accessKey=${message.accessKey}&emailHash=${message.customerEmailHash}`
            });

            setShowLLMLanding(true);
        }
    };

    const handleMessageClick = message => {
        queryString = "";
        setIsFabVisible(false);
        history.push({ pathname: "/contactusscreen" });
        if (showLLMLanding) {
            setShowLLMLanding(false);
        }

        setMessageToHandle(message);
    };

    const renderLoader = React.useMemo(
        () =>
            isLoading && (
                <Grid container justifyContent="center">
                    <LoadingSpinner />
                </Grid>
            ),
        [isLoading]
    );

    const paperContent = React.useMemo(() => {
        if (!announcementRouteState) {
            if (selectedTabIndex === 0 && !showLLMLanding && !getInTouchScreen && !submitMessage && !isReceived) {
                return (
                    <>
                        <KnowledgeArticles />
                        {articleList.length > 0 && <Divider />}
                        <Typography variant="h6" mt={3} mb={2}>
                            {"Still Need Help?"}
                        </Typography>
                        <Button
                            size={"small"}
                            variant="contained"
                            onClick={() => {
                                setAnnouncementRouteState(null);
                                setGetinTouchScreen(true);
                            }}
                        >
                            {"Get in touch"}
                        </Button>
                    </>
                );
            }
            if (selectedTabIndex === 0 && !isFabVisible && !submitMessage && !isReceived && getInTouchScreen) {
                return (
                    <GetInTouchScreen
                        supportedGroups={supportedGroups}
                        privateSupport={privateSupport}
                        messageId={messageId}
                    />
                );
            }
            if (selectedTabIndex === 0 && submitMessage && !showLLMLanding && !isReceived) {
                return <ReceiveMessageScreen handleConversationHistory={handleConversationHistory} />;
            }
            if (selectedTabIndex === 1) {
                return (
                    <>
                        {showLLMLanding && (
                            <div
                                id="limitless-root"
                                data-app-visible="always"
                                data-persistence="false"
                                data-min-question-char-count={10}
                                ref={llmRootRef}
                                data-new-conversation-mode={"group-new-tab"}
                                data-how-it-works={announcementRouteState ? "optional" : "disabled"}
                            />
                        )}
                        {(selectedTabIndex === 1 || showLLMLanding) && (
                            <div
                                id="limitless-landing-root"
                                data-client-key={apiKey}
                                data-tenant-name={tenantName}
                                data-group-name={groupName}
                                data-locale={locale}
                                data-username={firstName && lastName ? `${firstName} ${lastName}` : ""}
                                data-email={email}
                                data-customer-secured-data={JSON.stringify(customerSecuredData)}
                                data-client-theme-mode={isDarkMode ? "dark" : "light"}
                                ref={llmLandingRootRef}
                                style={selectedTabIndex === 1 ? { display: "none" } : {}}
                            />
                        )}
                        {selectedTabIndex === 0 && renderLoader}

                        {(isFabVisible || selectedTabIndex === 1) && (
                            <>
                                {!announcementRouteState && renderLoader}
                                <ConversationHistory handleMessageClick={handleMessageClick} />
                            </>
                        )}
                    </>
                );
            }
        } else {
            if (selectedTabIndex === 1) {
                return (
                    <RespondAnnouncement
                        setSelectedTabIndex={setSelectedTabIndex}
                        handleSubmit={handleSubmitAnnouncementMessage}
                    />
                );
            } else {
                return <ReceiveMessageScreen handleConversationHistory={handleConversationHistory} />;
            }
        }

        return null;
    }, [
        selectedTabIndex,
        articleList,
        showLLMLanding,
        isDarkMode,
        isLoading,
        isFabVisible,
        submitMessage,
        isReceived,
        getInTouchScreen,
        announcementRouteState,
        announcementMessage,
        queryDescription,
        queryTopic,
        hasPressed,
        announcementBody,
        error
    ]);

    return (
        <>
            <ThemeProvider theme={theme}>
                <VerticalTabWithPaperContainer tabContent={tabContent} paperContent={paperContent} />
            </ThemeProvider>
        </>
    );
};

export default ContactUsScreen;
